import { Injectable, inject } from '@angular/core';

import moment from 'moment-timezone';

import { ENVIRONMENT, LocalStorageService } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import { SupportedLocale } from '@core/languages-legacy';

enum Environments {
  Production = 'production',
  Rc = 'rc',
  Staging = 'staging',
  Development = 'development',
}

const LS_STARTED_AT = 'gettingStartedStartedAt';
const LS_SURVEY_DISPLAYED = 'gettingStartedDisplayed'; // old flag
const LS_SURVEY_DISPLAYED_V2 = 'gettingStartedDisplayedV2';
const LS_SURVEY_DISPLAYED_V3 = 'gettingStartedDisplayedV3';

@Injectable({ providedIn: 'root' })
export class GettingStartedSurveyService {
  private readonly i18n = inject(I18nService);
  private readonly intercomService = inject(IntercomService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly environment = inject<{
    environmentName: 'production' | 'rc' | 'staging' | 'development';
  }>(ENVIRONMENT, { optional: true });

  private readonly gettingStartedSurvey: Partial<
    Record<Environments, Partial<Record<SupportedLocale, number>>>
  > = {
    staging: {
      en: 40182738,
    },
    production: {
      en: 40253538,
      de: 40253686,
      fr: 40253784,
      nl: 40253829,
    },
  };

  public surveyInit() {
    if (this.checkIfSurveyWasDisplayed()) return;
    if (this.checkIfSurveyShouldDisplayed() || this.checkSurveyPeriodElapsed()) {
      this.openSurvey(this.getSurveys());
    }
  }

  public markGettingStartedAsStarted() {
    if (this.checkIfSurveyWasDisplayed()) return;
    if (this.localStorageService.get(LS_STARTED_AT)) return;
    this.localStorageService.set(LS_STARTED_AT, moment().toISOString());
  }

  private markSurveyAsDisplayed() {
    this.localStorageService.set(LS_SURVEY_DISPLAYED_V3, true);
    this.localStorageService.remove(LS_SURVEY_DISPLAYED);
    this.localStorageService.remove(LS_SURVEY_DISPLAYED_V2);
    this.localStorageService.remove(LS_STARTED_AT);
  }

  // check if the survey old flag was set in the past
  private checkIfSurveyShouldDisplayed(): boolean {
    return (
      !!this.localStorageService.get(LS_SURVEY_DISPLAYED) ||
      !!this.localStorageService.get(LS_SURVEY_DISPLAYED_V2)
    );
  }

  // check if the survey was displayed in the past
  private checkIfSurveyWasDisplayed(): boolean {
    return !!this.localStorageService.get(LS_SURVEY_DISPLAYED_V3);
  }

  private checkSurveyPeriodElapsed(): boolean {
    const startDate = this.localStorageService.get(LS_STARTED_AT);
    if (!startDate) return false;

    const duration = moment.duration(moment().diff(moment(startDate)));
    const days = duration.asDays();
    const timeOffsetToDisplay = 13;

    return days > timeOffsetToDisplay;
  }

  private openSurvey(surveyId: number | undefined) {
    if (surveyId) {
      this.markSurveyAsDisplayed();
      this.intercomService.scheduleSurvey(surveyId);
      return;
    }
    console.error('Survey ID is not defined');
  }

  private matchLocale(locale: SupportedLocale): SupportedLocale {
    return [SupportedLocale.Swedish, SupportedLocale.Finnish].includes(locale)
      ? SupportedLocale.English
      : locale;
  }

  private getSurveys(): number {
    const { production, staging } = this.gettingStartedSurvey;
    const locale = this.matchLocale(<SupportedLocale>this.i18n.locale);

    if (this.environment?.environmentName === 'production') return production![locale]!;
    if (this.environment?.environmentName === 'rc') return production![locale]!;
    return staging!['en']!;
  }
}
